import { z } from 'zod';
import { EMPLOYEE_COUNT_MAX } from '../limits.js';
import { EmailSchema, HistoryEntrySchema, IDSchema, IsoTimestampSchema, SchemaVersionSchema, } from './shared.js';
export const AccountStateSchema = z.enum([
    'lead',
    'engaged',
    'active_customer',
    'inactive_customer',
    'lost',
]);
export const AccountSourceSchema = z.enum([
    'manual',
    'webhook',
    'bulk_import',
    'referral',
    'conference',
    'cold_outreach',
    'web_form',
    'partner',
    'other',
]);
const ReassignmentEntrySchema = z.object({
    fromRepEmail: EmailSchema.optional(),
    toRepEmail: EmailSchema,
    at: IsoTimestampSchema,
    by: z.string(),
    reason: z.string().optional(),
});
export const AccountSchema = z.object({
    // ─── Identity ───
    id: IDSchema,
    type: z.literal('account'),
    schemaVersion: SchemaVersionSchema,
    // ─── Lifecycle ───
    state: AccountStateSchema,
    stateChangedAt: IsoTimestampSchema,
    stateChangedBy: z.string(),
    stateChangeReason: z.string().optional(),
    // ─── Company info ───
    companyName: z.string().min(1).max(200),
    companyDomain: z.string().optional(),
    companyDomainNormalized: z.string().optional(),
    industry: z.string().optional(),
    // QA 2026-10-01 F-08 — same ceiling as the account routes.
    employeeCount: z.number().int().nonnegative().max(EMPLOYEE_COUNT_MAX).optional(),
    // T3-014C — Address fields. All optional. Rendered in Quote PDF
    // PREPARED FOR block when set; lines omit gracefully when null.
    addressLine1: z.string().max(200).optional(),
    addressLine2: z.string().max(200).optional(),
    city: z.string().max(100).optional(),
    mailingState: z.string().optional(),
    postalCode: z.string().max(20).optional(),
    // RIG-399 — company phone (Apollo `Company Phone`); the account's own number.
    phone: z.string().max(50).optional(),
    customerTimezone: z.string().optional(),
    // ─── Source attribution ───
    source: AccountSourceSchema,
    sourceDetail: z.string().optional(),
    sourceCampaign: z.string().optional(),
    sourceContactName: z.string().optional(),
    // ─── Assignment ───
    assignedRepEmail: EmailSchema.optional(),
    assignedAt: IsoTimestampSchema.optional(),
    assignedBy: z.string().optional(),
    reassignmentHistory: z.array(ReassignmentEntrySchema).default([]),
    // ─── Customer portal ───
    portalEnabled: z.boolean().default(false),
    portalLastAccessedAt: IsoTimestampSchema.optional(),
    // ─── QuickBooks Online linkage ───
    // T7-005 / T7-006 — QBO Customer entity ID. When set, the
    // "Generate invoice" admin action supplies this as `customerRef` to the
    // QBO API. When null, the invoice-generate route rejects with 400.
    // Populated lazily (V1 ships the column; V1.5 candidate admin UI lets
    // an operator link an Account to a QBO Customer).
    qboCustomerId: z.string().optional(),
    // T-STRIPE-CHECKOUT — Stripe Customer id (cus_…). Created lazily on first
    // Proposal PDF generation; reused for subsequent invoices on the account.
    stripeCustomerId: z.string().optional(),
    // ─── Merge lineage (T10A-MERGE) ───
    // When this Account was merged INTO a survivor as the LOSER, this points at
    // the survivor's id (and `deletedAt` is set). NULL for every live Account and
    // every survivor. Set atomically by the account-merge workflow alongside the
    // soft-delete, so "where did this duplicate go?" is queryable forever.
    mergedIntoAccountId: z.string().optional(),
    // ─── Operational ───
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    deletedAt: IsoTimestampSchema.nullable().optional(),
    deletedBy: z.string().optional(),
    deletedReason: z.string().optional(),
    // ─── Optimistic concurrency ───
    version: z.number().int().positive(),
    // ─── Optional metadata ───
    tags: z.array(z.string()).default([]),
    notes: z.string().optional(),
    // ─── History projection ───
    history: z.array(HistoryEntrySchema).default([]),
});
//# sourceMappingURL=account.js.map